import { api } from './api';
import type {
  ApiEnvelope, AuditFields, EngineListParams, PaginatedEnvelope,
} from './notification-engine.types';

/**
 * The notification engine's own definitions (`/api/v1/notification-masters`,
 * bare REST paths). The FE route family is `/notification-masters`, matching
 * this API.
 */
const BASE = '/api/v1/notification-masters';

/** A payload variable the notification declares and its templates reference. */
export interface ParameterDefinition {
  name: string;
  dataType: string;
  isRequired?: boolean;
  defaultValue?: unknown;
  sample?: unknown;
  description?: string | null;
}

/** How many of the people a rule resolved actually receive it. */
export interface RuleSelection {
  mode?: string;                    // ALL | TOP_N
  topN?: number | null;
  /** `<field> ASC|DESC` — ranks on an attribute the resolution function emitted. */
  rankingExpression?: string | null;
}

/** Where to look when a rule resolves to nobody — an ordered ladder. */
export interface RuleFallback {
  enabled?: boolean;
  chain?: string[];
}

/**
 * One audience of a notification, and how it is treated HERE.
 *
 * `selection` and `fallback` describe this usage rather than the recipient
 * itself, so the same recipient can be the main audience of one notification
 * and a fallback on another without being copied.
 */
export interface Recipient {
  recipientMasterId: string;
  /** A mandatory entry resolving to nobody fails the whole send. */
  isMandatory?: boolean;
  selection?: RuleSelection;
  fallback?: RuleFallback;
}

/**
 * A new_notification_master row.
 *
 * Carries no code of its own — it inherits its type's. Two places to read one
 * code would eventually disagree.
 */
export interface NotificationMaster extends AuditFields {
  _id: string;
  notificationTypeId: string;
  description?: string | null;
  channelTemplates?: Record<string, unknown>;
  parameters?: ParameterDefinition[];
  conditionTree?: Record<string, unknown> | null;
  recipients?: Recipient[];
}

export type NotificationMasterCreateRequest =
  Omit<NotificationMaster, '_id' | keyof AuditFields> & { appId?: string | null };
export type NotificationMasterUpdateRequest = Partial<NotificationMasterCreateRequest>;

export interface TriggerRequest {
  payload?: Record<string, unknown>;
  context?: Record<string, unknown>;
  correlationId?: string;
}

export const notificationMastersService = {
  list: async (params?: EngineListParams): Promise<PaginatedEnvelope<NotificationMaster>> => {
    const response = await api.get<PaginatedEnvelope<NotificationMaster>>(BASE, { params });
    return response.data;
  },
  /**
   * Read one, through the listing (`?id=`).
   *
   * The engine now also serves `GET ${BASE}/${id}` (the retired notification
   * router used to shadow it); `?id=` is kept because it works against backends
   * deployed before and after that change.
   */
  getById: async (id: string): Promise<NotificationMaster> => {
    const response = await api.get<PaginatedEnvelope<NotificationMaster>>(
      BASE, { params: { id, limit: 1 } });
    const row = (response.data.data || [])[0];
    if (!row) throw new Error('Notification not found');
    return row;
  },
  create: async (data: NotificationMasterCreateRequest): Promise<NotificationMaster> => {
    const response = await api.post<ApiEnvelope<NotificationMaster>>(BASE, data);
    return response.data.data;
  },
  update: async (id: string, data: NotificationMasterUpdateRequest): Promise<NotificationMaster> => {
    const response = await api.put<ApiEnvelope<NotificationMaster>>(`${BASE}/${id}`, data);
    return response.data.data;
  },
  /**
   * Where correctness is enforced: every FK resolves, every parameter has a
   * source, every channel has an `en` template, and each chain type-checks.
   */
  /** Retire without deleting: the runtime reads only active rows. */
  deactivate: async (id: string): Promise<NotificationMaster> => {
    const response = await api.post<ApiEnvelope<NotificationMaster>>(`${BASE}/${id}/deactivate`);
    return response.data.data;
  },
  /** Fire it now, by hand — the manual trigger source. */
  trigger: async (id: string, body: TriggerRequest): Promise<Record<string, unknown>> => {
    const response = await api.post<ApiEnvelope<Record<string, unknown>>>(`${BASE}/${id}/trigger`, body);
    return response.data.data;
  },
  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiEnvelope<{ message: string }>>(`${BASE}/${id}`);
    return response.data.data;
  },
};
